import * as $ from 'jquery';
import { IDisposable, IEventAggregator, disposableSubscription } from 'common/barrels/core';
import { Observable, AsyncSubject, ISubscription } from 'common/barrels/rx';
import { dateTimeTransmissionFormat } from 'common/functions/date';
import { Serializer } from 'common/serializer';
import { IResourceProvider } from 'common/resourceprovider';
import { Request } from 'common/request';
import { SystemError } from 'common/exceptions/systemerror';

import { ValidationFailed } from 'shared/data/validation';
import { User } from 'shared/data/user';

import { ControlArgs } from 'runtime/controls/controlargs';
import { IViewModelRoot } from 'runtime/controls/control';
import { ValidationControl } from 'runtime/controls/validationcontrol';
import { Screen } from 'runtime/screens/screen';
import { Busy } from 'runtime/events/busy';
import { NotBusy } from 'runtime/events/notbusy';
import { CurrentUserDelegation } from 'runtime/data/currentuserdelegation';
import { LookupItem } from 'runtime/data/lookupitem';

import * as path from 'shared/path';
import * as moment from 'moment';
import * as ko from 'knockout';
import { Observable as KnockoutObservable, observable as koObservable, observableArray as koObservableArray, pureComputed as koPureComputed } from 'knockout';

import 'runtime/styles/screens/delegation.less';

const dateValueFormat = dateTimeTransmissionFormat();

export class Delegation extends Screen {
    private vm: DelegationViewModel;

    private validationControl: ValidationControl;

    private delegationDisposable: IDisposable;

    constructor($hostElement: JQuery, resourcesObservable: Observable<IResourceProvider>, parentEventAggregator: IEventAggregator, serializer: Serializer, csrfToken: () => string) {
        super($hostElement, resourcesObservable, parentEventAggregator, serializer, csrfToken);

        var $element = this.$element = $('<div />')
            .addClass('wf-container wf-delegation-container')
            .appendTo($hostElement)
            .on('remove', () => this.dispose());

        var source = this.loadCurrentUserDelegation()
            .switchMap(value => value !== null ? Observable.of<IDelegationViewModel>(value) : this.loadAllDelegateUsers());

        this.delegationDisposable = disposableSubscription(
            this.screenDisposable,

            this.eventAggregator.query(SetDelegationEvent).subscribe(event => this.setDelegation(event.args)),
            this.eventAggregator.query(CancelDelegationEvent).subscribe(event => this.cancelDelegation(event.delegationId)),
            Observable.zip(this.resourcesAvailable, source, (v1, v2) => v2).subscribe(
                value => {
                    this.buildDelegationContainer(this.$element, this.resources);

                    var vm = this.vm = new DelegationViewModel(this.eventAggregator, this.resources, value);

                    ko.applyBindings(vm, $element[0]);

                    this.eventAggregator.publish(new NotBusy());
                })
        );
    }

    dispose(): void {
        this.delegationDisposable.dispose();
    }

    remove(): void {
        this.$element.remove();
        this.dispose();
    }

    private buildDelegationContainer($element: JQuery, resources: IResourceProvider): void {
        $element.append(
            $('<div />').addClass('wf-outer').append([
                $('<div />').addClass('wf-header').append(
                    $('<div />').append([
                        $('<div />').addClass('wf-filter').append([
                            $('<div />').addClass('wf-filter-label').text(resources.getCoreString('XamlDateFrom')),
                            $('<div />').addClass('wf-filter-control').append([
                                $('<div />').addClass('wf-delegation-date').append([
                                    $('<div />').attr('data-bind', 'disabled: hasCurrentDelegation, dateTimePicker: { selectedDate: fromDate }')
                                ])
                            ])
                        ]),
                        $('<div />').addClass('wf-filter').append([
                            $('<div />').addClass('wf-filter-label').text(resources.getCoreString('XamlDateTo')),
                            $('<div />').addClass('wf-filter-control').append([
                                $('<div />').addClass('wf-delegation-date').append([
                                    $('<div />').attr('data-bind', 'disabled: hasCurrentDelegation, dateTimePicker: { selectedDate: toDate }')
                                ])
                            ])
                        ]),
                        $('<div />').addClass('wf-filter').append([
                            $('<div />').addClass('wf-filter-label').text(resources.getCoreString('XamlUser')),
                            $('<div />').addClass('wf-filter-control').append([
                                $('<div />').addClass('wf-delegation-user-list').append([
                                    $('<div />').attr('data-bind', 'disabled: hasCurrentDelegation, dropDownList: { selectedValue: delegateToUserId, context: \'delegateUser\', lookupItems: getDelegateUsers, lookupItem: getDelegateUser }')
                                ])
                            ])
                        ]),
                        $('<div />').addClass('wf-filter').append([
                            $('<div />').addClass('wf-filter-label').html('&#160;'),
                            $('<div />').addClass('wf-filter-control').append([
                                $('<div />').addClass('wf-delegation-set').attr('data-bind', 'visible: (hasCurrentDelegation() === false)').append([
                                    $('<div />').attr('data-bind', `enabled: canSetDelegation, activityButton: { click: setDelegation, text: \'${resources.getCoreString('XamlSetDelegation')}\' }`)
                                ]),
                                $('<div />').addClass('wf-delegation-cancel').attr('data-bind', 'visible: hasCurrentDelegation').append([
                                    $('<div />').attr('data-bind', `enabled: canCancelDelegation, activityButton: { click: cancelDelegation, text: \'${resources.getCoreString('XamlCancelDelegation')}\' }`)
                                ])
                            ])
                        ])
                    ])
                ),
                $('<div />').addClass('wf-inner'),
                $('<div />').addClass('wf-footer')
            ])
        );
    }

    private loadCurrentUserDelegation(): Observable<IDelegationViewModel> {
        var asyncSubject = new AsyncSubject<CurrentUserDelegation>();

        Request.create(this.serializer, path.api('delegation/user'))
            .before(() => this.eventAggregator.publish(new Busy()))
            .handle(CurrentUserDelegation, value => (asyncSubject.next(value), asyncSubject.complete()))
            .handle(null, () => (asyncSubject.next(null), asyncSubject.complete()))
            .handle(SystemError, t => this.systemError(t))
            .error((jqXhr, textStatus, errorThrown) => this.httpError(errorThrown, textStatus))
            .final(() => this.eventAggregator.publish(new NotBusy()))
            .get();

        return asyncSubject.map(this.mapCurrentUserDelegationToViewModel);
    }

    private loadAllDelegateUsers(): Observable<IDelegationViewModel> {
        var asyncSubject = new AsyncSubject<User[]>();

        Request.create(this.serializer, path.api('delegation'))
            .before(() => this.eventAggregator.publish(new Busy()))
            .handleArray(User, users => (asyncSubject.next(users), asyncSubject.complete()))
            .handle(null, () => (asyncSubject.next([]), asyncSubject.complete()))
            .handle(SystemError, t => this.systemError(t))
            .error((jqXhr, textStatus, errorThrown) => this.httpError(errorThrown, textStatus))
            .final(() => this.eventAggregator.publish(new NotBusy()))
            .get();

        return asyncSubject.map(this.mapUsersToViewModel);
    }

    private setDelegation(args: ISetDelegationArgs): void {
        var loadCurrentUserDelegation = (): void => {
            var subscription: ISubscription = null;
            subscription = this.loadCurrentUserDelegation().switchMap(value => value !== null ? Observable.of<IDelegationViewModel>(value) : this.loadAllDelegateUsers()).subscribe(value => {
                this.vm.setData(value);
                subscription.unsubscribe();
            });
        };

        Request.create(this.serializer, path.api(`delegation/${args.delegateToUserId}/set`))
            .args(() => ({ from: args.fromDate, to: args.toDate }))
            .before(() => this.eventAggregator.publish(new Busy()))
            .handle(null, () => loadCurrentUserDelegation())
            .handle(ValidationFailed, t => {
                if (this.validationControl === undefined || this.validationControl === null) {
                    var $div = $('.wf-header > div', this.$element);
                    var ctrlArgs = ControlArgs.createMock($div, { elementName: 'wfdelegationvalidation' }, this.eventAggregator, null, this.resources);

                    this.validationControl = new ValidationControl(ctrlArgs);
                }
                this.validationControl.validationSummary = t.validationSummary;
                this.validationControl.render();
            })
            .handle(SystemError, t => this.systemError(t))
            .error((jqXhr, textStatus, errorThrown) => this.httpError(errorThrown, textStatus))
            .final(() => this.eventAggregator.publish(new NotBusy()))
            .post(this.csrfToken());
    }

    private cancelDelegation(delegationId: string): void {
        var loadAllDelegateUsers = (): void => {
            var subscription: ISubscription = null;
            subscription = this.loadAllDelegateUsers().subscribe(value => {
                this.vm.setData(value);
                subscription.unsubscribe();
            });
        };

        Request.create(this.serializer, path.api(`delegation/${delegationId}/cancel`))
            .before(() => this.eventAggregator.publish(new Busy()))
            .handle(null, () => loadAllDelegateUsers())
            .handle(SystemError, t => this.systemError(t))
            .error((jqXhr, textStatus, errorThrown) => this.httpError(errorThrown, textStatus))
            .final(() => this.eventAggregator.publish(new NotBusy()))
            .post(this.csrfToken());
    }

    private mapCurrentUserDelegationToViewModel(value: CurrentUserDelegation): IDelegationViewModel {
        return (value === undefined || value === null) ? null : {
            fromDate: moment.utc(value.userDelegation.validFrom, dateValueFormat).local().format(dateValueFormat),
            toDate: moment.utc(value.userDelegation.validTo, dateValueFormat).local().format(dateValueFormat),
            delegateToUserId: value.userDelegation.delegateToUserId,
            delegationId: value.userDelegation.delegationId,
            delegateUsers: [value.delegateUser]
        };
    }

    private mapUsersToViewModel(value: User[]): IDelegationViewModel {
        return {
            fromDate: moment().startOf('day').format(dateValueFormat),
            toDate: moment().endOf('day').add(1, 'day').format(dateValueFormat),
            delegateToUserId: null,
            delegationId: null,
            delegateUsers: value || []
        };
    }
}

class SetDelegationEvent {
    constructor(public args: ISetDelegationArgs) { }

    static eventName() { return 'SetDelegationEvent'; }

    eventName = SetDelegationEvent.eventName;
}

class CancelDelegationEvent {
    constructor(public delegationId: string) { }

    static eventName() { return 'CancelDelegationEvent'; }

    eventName = CancelDelegationEvent.eventName;
}

interface ISetDelegationArgs {
    fromDate: string;
    toDate: string;
    delegateToUserId: string;
}

interface IDelegationViewModel extends ISetDelegationArgs {
    delegationId: string;
    delegateUsers: User[];
}

interface IDelegateUserViewModel {
    userId: KnockoutObservable<string>;
    displayName: KnockoutObservable<string>;
}

class DelegationViewModel implements IViewModelRoot {
    eventAggregator: IEventAggregator;
    resources: IResourceProvider;

    fromDate = koObservable<string>();
    toDate = koObservable<string>();
    delegateToUserId = koObservable<string>();

    private delegationId = koObservable<string>();
    private delegateUsers = koObservableArray<User>();

    constructor(eventAggregator: IEventAggregator, resources: IResourceProvider, jsObject: IDelegationViewModel) {
        this.eventAggregator = eventAggregator;
        this.resources = resources;

        this.setData(jsObject);
    }

    hasCurrentDelegation = koPureComputed(() => this.delegationId() !== null);

    canSetDelegation = koPureComputed(() => !this.hasCurrentDelegation() && this.fromDate() < this.toDate() && this.delegateToUserId() !== null);

    canCancelDelegation = koPureComputed(() => this.hasCurrentDelegation());

    setData(jsObject: IDelegationViewModel): void {
        // manually set the knockout observables, so delegateUsers is set before delegateToUserId
        this.delegateUsers(jsObject.delegateUsers);

        this.fromDate(jsObject.fromDate);
        this.toDate(jsObject.toDate);
        this.delegateToUserId(jsObject.delegateToUserId);
        this.delegationId(jsObject.delegationId);
    }

    setDelegation(): void {
        var args: ISetDelegationArgs = {
            fromDate: moment(this.fromDate(), dateValueFormat).utc().format(dateValueFormat),
            toDate: moment(this.toDate(), dateValueFormat).utc().format(dateValueFormat),
            delegateToUserId: this.delegateToUserId()
        };
        this.eventAggregator.publish(new SetDelegationEvent(args));
    }

    cancelDelegation(): void {
        var delegationId = this.delegationId();
        this.eventAggregator.publish(new CancelDelegationEvent(delegationId));
    }

    getDelegateUsers(): LookupItem[] {
        return this.delegateUsers !== undefined && this.delegateUsers !== null
            ? this.delegateUsers().map(u => this.userAsLookupItem(u))
            : [];
    }

    getDelegateUser(keyValue: string): LookupItem {
        return (this.hasCurrentDelegation() && keyValue !== undefined)
            ? this.delegateUsers().map(u => this.userAsLookupItem(u)).filter(i => i.keyValue === keyValue)[0] || null
            : null;
    }

    private userAsLookupItem(u: User): LookupItem {
        return {
            keyValue: u.userId,
            displayValue: u.displayName,
            properties: null, //LookupProperty[]
            parentKeyValue: null, //string
            active: true //boolean
        };
    }
}